
/**
 * Hook otimizado para buscar leads com paginação infinita e busca server-side
 * 
 * Este hook substitui o useLeads tradicional na página de contatos para:
 * - Carregar apenas os leads necessários (paginação)
 * - Buscar leads no servidor mesmo que não estejam na página atual
 * - Aplicar filtros diretamente no banco de dados
 * - Melhorar performance com grandes volumes de dados
 * 
 * Utiliza useInfiniteQuery do TanStack Query para:
 * - Carregar dados em páginas conforme necessário
 * - Cache inteligente de resultados
 * - Controle de estados de loading
 * - Invalidação automática de cache
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Lead } from './useSupabaseLeads';

// Interface para parâmetros de busca e filtros
interface LeadsSearchParams {
  searchQuery?: string;
  tagId?: string | null;
  origemLead?: string | null;
  servicoInteresse?: string | null;
  etapaId?: string | null;
  apenasAnuncios?: boolean;     // Novo: filtro para mostrar apenas leads de anúncios
  adName?: string | null;       // Novo: filtro por nome específico do anúncio
  adPlatform?: string | null;   // Novo: filtro por plataforma do anúncio
  sortField?: 'nome' | 'created_at' | 'email';
  sortOrder?: 'asc' | 'desc';
  pageSize?: number;
}

// Interface para parâmetros específicos de clínica (modo admin)
interface UseLeadsInfiniteProps extends LeadsSearchParams {
  adminMode?: boolean;
  targetClinicaId?: string | null;
}

// Interface para dados retornados pelo hook principal
interface LeadsPageData {
  leads: Lead[];
  nextPage: number | null;
  totalCount: number;
}

// Tamanho padrão da página para carregamento
const DEFAULT_PAGE_SIZE = 50;

/**
 * Hook principal para busca paginada de leads
 */
export const useLeadsInfinite = ({
  searchQuery = '',
  tagId = null,
  origemLead = null,
  servicoInteresse = null,
  etapaId = null,
  apenasAnuncios = false,
  adName = null,
  adPlatform = null,
  sortField = 'created_at',
  sortOrder = 'desc',
  pageSize = DEFAULT_PAGE_SIZE,
  adminMode = false,
  targetClinicaId = null
}: UseLeadsInfiniteProps = {}) => {

  return useInfiniteQuery({
    queryKey: [
      'leads-infinite',
      searchQuery,
      tagId,
      origemLead,
      servicoInteresse,
      etapaId,
      apenasAnuncios,
      adName,
      adPlatform,
      sortField,
      sortOrder,
      adminMode,
      targetClinicaId,
      pageSize
    ],
    queryFn: async ({ pageParam = 0 }): Promise<LeadsPageData> => {
      console.log('Buscando leads - Página:', pageParam, 'Busca:', searchQuery);

      // Calcular offset para paginação
      const from = (pageParam as number) * pageSize;
      const to = from + pageSize - 1;

      // Construir query base com select parcial otimizado
      // Selecionando apenas os 17 campos necessários vs 37 totais (~54% redução)
      let query = supabase
        .from('leads')
        .select(`
          id,
          nome,
          email,
          telefone,
          created_at,
          updated_at,
          tag_id,
          origem_lead,
          servico_interesse,
          etapa_kanban_id,
          ad_name,
          ad_platform,
          avatar_url,
          nome_clinica,
          anotacoes,
          data_ultimo_contato,
          convertido
        `, { count: 'exact' });

      // Sempre filtra por clínica quando o id existe.
      // Motivo: a RLS sozinha (clinica_id = função() OR is_admin()) impede o índice e estoura o timeout.
      if (targetClinicaId) {
        query = query.eq('clinica_id', targetClinicaId);
      }

      // Aplicar busca por texto (nome, email, telefone)
      if (searchQuery && searchQuery.trim()) {
        const searchTerm = `%${searchQuery.trim()}%`;
        query = query.or(`nome.ilike.${searchTerm},email.ilike.${searchTerm},telefone.ilike.${searchTerm}`);
      }

      // Aplicar filtros específicos
      if (tagId) {
        query = query.eq('tag_id', tagId);
      }

      if (origemLead) {
        query = query.eq('origem_lead', origemLead);
      }

      if (servicoInteresse) {
        query = query.eq('servico_interesse', servicoInteresse);
      }

      if (etapaId) {
        query = query.eq('etapa_kanban_id', etapaId);
      }

      // Filtros específicos para anúncios
      if (apenasAnuncios) {
        // Filtrar apenas leads que vieram de anúncios
        query = query.or('ad_name.neq.null,anuncio.eq.sim');
      }

      if (adName) {
        query = query.eq('ad_name', adName);
      }

      if (adPlatform) {
        query = query.eq('ad_platform', adPlatform);
      }

      // Aplicar ordenação
      query = query.order(sortField, { ascending: sortOrder === 'asc' });

      // Aplicar paginação
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error('Erro ao buscar leads:', error);
        throw new Error(`Erro ao buscar leads: ${error.message}`);
      }

      // Mapear dados para incluir campos de compatibilidade
      const mappedLeads = (data || []).map((lead: any): Lead => ({
        ...lead,
        etapa_id: lead.etapa_kanban_id,
        avatar_url: lead.avatar_url || null,
        nome_clinica: lead.nome_clinica || null,
        ad_name: lead.ad_name || null
      }));

      // Determinar se há próxima página
      const hasNextPage = mappedLeads.length === pageSize && (from + pageSize) < (count || 0);
      const nextPage = hasNextPage ? (pageParam as number) + 1 : null;

      console.log(`Carregados ${mappedLeads.length} leads. Total: ${count}. Próxima página: ${nextPage}`);

      return {
        leads: mappedLeads,
        nextPage,
        totalCount: count || 0
      };
    },
    getNextPageParam: (lastPage: LeadsPageData) => lastPage.nextPage,
    initialPageParam: 0, // Parâmetro obrigatório na nova versão do TanStack Query
    // Sem clínica o filtro some e o Postgres varre a tabela inteira até o timeout.
    enabled: Boolean(targetClinicaId),
    staleTime: 30000, // Cache por 30 segundos
    // Resetar páginas quando parâmetros de busca mudarem
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook simplificado para obter contagem total de leads
 * Útil para estatísticas sem carregar todos os dados
 * 
 * NOTA: Removida contagem HEAD para evitar timeouts em tabelas grandes
 * A contagem vem da query principal com { count: 'exact' }
 */
export const useLeadsCount = ({
  searchQuery = '',
  tagId = null,
  origemLead = null,
  servicoInteresse = null,
  etapaId = null,
  apenasAnuncios = false,
  adName = null,
  adPlatform = null,
  adminMode = false,
  targetClinicaId = null
}: UseLeadsInfiniteProps = {}) => {

  return useQuery({
    queryKey: [
      'leads-count',
      searchQuery,
      tagId,
      origemLead,
      servicoInteresse,
      etapaId,
      apenasAnuncios,
      adName,
      adPlatform,
      adminMode,
      targetClinicaId
    ],
    queryFn: async (): Promise<{ totalCount: number }> => {
      console.log('Contando leads com filtros aplicados');

      // Construir query apenas para contagem (limita a 1 registro para performance)
      let query = supabase
        .from('leads')
        .select('id', { count: 'exact' })
        .limit(1); // Apenas conta, não precisa buscar dados

      // Sem o id da clínica a contagem também varre a tabela inteira e toma timeout.
      if (targetClinicaId) {
        query = query.eq('clinica_id', targetClinicaId);
      }

      // Aplicar busca por texto
      if (searchQuery && searchQuery.trim()) {
        const searchTerm = `%${searchQuery.trim()}%`;
        query = query.or(`nome.ilike.${searchTerm},email.ilike.${searchTerm},telefone.ilike.${searchTerm}`);
      }

      // Aplicar filtros específicos
      if (tagId) query = query.eq('tag_id', tagId);
      if (origemLead) query = query.eq('origem_lead', origemLead);
      if (servicoInteresse) query = query.eq('servico_interesse', servicoInteresse);
      if (etapaId) query = query.eq('etapa_kanban_id', etapaId);

      // Aplicar filtros de anúncios
      if (apenasAnuncios) {
        query = query.or('ad_name.neq.null,anuncio.eq.sim');
      }
      if (adName) query = query.eq('ad_name', adName);
      if (adPlatform) query = query.eq('ad_platform', adPlatform);

      const { error, count } = await query;

      if (error) {
        console.error('Erro ao contar leads:', error);
        // Fallback: retornar 0 em caso de erro ao invés de travar
        console.warn('⚠️ Usando fallback count = 0 devido a erro');
        return { totalCount: 0 };
      }

      return { totalCount: count || 0 };
    },
    enabled: Boolean(targetClinicaId),
    staleTime: 60000, // Cache por 1 minuto
    refetchOnWindowFocus: false,
  });
};

/**
 * Tamanho do lote para exportação paginada
 * O Supabase/PostgREST tem limite padrão de ~1000 registros por requisição.
 * Buscamos em lotes para contornar esse limite e permitir exportação ilimitada.
 */
const EXPORT_BATCH_SIZE = 1000;

/**
 * Interface para resultado de exportação com progresso
 */
interface ExportResult {
  leads: Lead[];
  totalCount: number;
}

/**
 * Interface para parâmetros do hook de exportação
 * - exportScope: 'filtered' aplica filtros atuais, 'all' ignora filtros e exporta tudo
 */
interface UseLeadsForExportParams extends UseLeadsInfiniteProps {
  enabled: boolean;
  exportScope?: 'filtered' | 'all';
  onProgress?: (loaded: number, total: number) => void;
}

/**
 * Hook para exportar leads com paginação por lotes
 * 
 * COMO FUNCIONA:
 * 1. Primeiro busca a contagem total de leads (sem trazer dados)
 * 2. Depois busca em lotes de EXPORT_BATCH_SIZE (1000) usando .range()
 * 3. Concatena todos os lotes até acabar
 * 4. Permite acompanhar progresso via callback onProgress
 * 
 * MOTIVO: O Supabase/PostgREST limita a ~1000 registros por query.
 * Com paginação por lotes, conseguimos exportar quantidade ilimitada.
 */
export const useLeadsForExport = (params: UseLeadsForExportParams) => {
  const {
    enabled,
    exportScope = 'filtered', // 'filtered' usa os filtros atuais, 'all' ignora filtros
    onProgress,
    searchQuery = '',
    tagId = null,
    origemLead = null,
    servicoInteresse = null,
    etapaId = null,
    apenasAnuncios = false,
    adName = null,
    adPlatform = null,
    sortField = 'created_at',
    sortOrder = 'desc',
    adminMode = false,
    targetClinicaId = null
  } = params;

  return useQuery({
    queryKey: [
      'leads-export',
      exportScope,
      searchQuery,
      tagId,
      origemLead,
      servicoInteresse,
      etapaId,
      apenasAnuncios,
      adName,
      adPlatform,
      sortField,
      sortOrder,
      adminMode,
      targetClinicaId
    ],
    queryFn: async (): Promise<ExportResult> => {
      console.log(`📤 [Exportação] Iniciando exportação - Escopo: ${exportScope}`);

      // ========================================
      // PASSO 1: Construir query base
      // A query base é reutilizada para contagem e busca
      // ========================================
      const buildBaseQuery = (selectFields: string, options: { count?: 'exact', head?: boolean } = {}) => {
        let query = supabase
          .from('leads')
          .select(selectFields, options);

        // Exportação também precisa do id da clínica para usar o índice e não estourar timeout.
        if (targetClinicaId) {
          query = query.eq('clinica_id', targetClinicaId);
        }

        // Se exportScope é 'all', ignoramos os filtros de busca/tag/etc
        // Se exportScope é 'filtered', aplicamos todos os filtros
        if (exportScope === 'filtered') {
          // Aplicar busca por texto
          if (searchQuery && searchQuery.trim()) {
            const searchTerm = `%${searchQuery.trim()}%`;
            query = query.or(`nome.ilike.${searchTerm},email.ilike.${searchTerm},telefone.ilike.${searchTerm}`);
          }

          // Aplicar filtros específicos
          if (tagId) query = query.eq('tag_id', tagId);
          if (origemLead) query = query.eq('origem_lead', origemLead);
          if (servicoInteresse) query = query.eq('servico_interesse', servicoInteresse);
          if (etapaId) query = query.eq('etapa_kanban_id', etapaId);

          // Aplicar filtros de anúncios
          if (apenasAnuncios) {
            query = query.or('ad_name.neq.null,anuncio.eq.sim');
          }
          if (adName) query = query.eq('ad_name', adName);
          if (adPlatform) query = query.eq('ad_platform', adPlatform);
        }

        return query;
      };

      // ========================================
      // PASSO 2: Buscar contagem total
      // Usamos head: true para não trazer dados, só a contagem
      // ========================================
      const countQuery = buildBaseQuery('id', { count: 'exact', head: true });
      const { count: totalCount, error: countError } = await countQuery;

      if (countError) {
        console.error('❌ [Exportação] Erro ao contar leads:', countError);
        throw new Error(`Erro ao contar leads: ${countError.message}`);
      }

      const total = totalCount || 0;
      console.log(`📊 [Exportação] Total de leads a exportar: ${total}`);

      // Se não há leads, retorna vazio
      if (total === 0) {
        return { leads: [], totalCount: 0 };
      }

      // ========================================
      // PASSO 3: Buscar em lotes usando .range()
      // Loop que busca EXPORT_BATCH_SIZE registros por vez
      // ========================================
      const allLeads: Lead[] = [];
      let currentOffset = 0;

      while (currentOffset < total) {
        // Calcular range do lote atual
        const from = currentOffset;
        const to = Math.min(currentOffset + EXPORT_BATCH_SIZE - 1, total - 1);

        console.log(`📦 [Exportação] Buscando lote: ${from} a ${to} de ${total}`);

        // Construir query para este lote
        let batchQuery = buildBaseQuery('*');

        // Aplicar ordenação estável (importante para paginação consistente)
        batchQuery = batchQuery
          .order(sortField, { ascending: sortOrder === 'asc' })
          .order('id', { ascending: true }) // Desempate por ID para ordenação estável
          .range(from, to);

        const { data, error } = await batchQuery;

        if (error) {
          console.error(`❌ [Exportação] Erro no lote ${from}-${to}:`, error);
          throw new Error(`Erro ao buscar leads (lote ${from}-${to}): ${error.message}`);
        }

        // Mapear dados para incluir campos de compatibilidade
        const mappedLeads = (data || []).map((lead: any): Lead => ({
          ...lead,
          etapa_id: lead.etapa_kanban_id,
          avatar_url: lead.avatar_url || null,
          nome_clinica: lead.nome_clinica || null,
          ad_name: lead.ad_name || null
        }));

        allLeads.push(...mappedLeads);

        // Atualizar progresso via callback (se fornecido)
        if (onProgress) {
          onProgress(allLeads.length, total);
        }

        // Avançar para próximo lote
        currentOffset += EXPORT_BATCH_SIZE;

        // Se recebemos menos registros que o esperado, não há mais dados
        if (mappedLeads.length < EXPORT_BATCH_SIZE) {
          break;
        }
      }

      console.log(`✅ [Exportação] Concluída! ${allLeads.length} leads carregados de ${total} total`);

      return {
        leads: allLeads,
        totalCount: total
      };
    },
    enabled, // Só executa quando habilitado explicitamente
    staleTime: 0, // Sempre buscar dados frescos para exportação
    gcTime: 0, // Não manter em cache após uso (era cacheTime em versões antigas)
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook para buscar anúncios e plataformas únicos disponíveis
 * Usado para popular os dropdowns de filtros de anúncios
 */
export const useAvailableAds = ({
  adminMode = false,
  targetClinicaId = null
}: {
  adminMode?: boolean;
  targetClinicaId?: string | null;
} = {}) => {

  return useQuery({
    queryKey: ['available-ads', adminMode, targetClinicaId],
    queryFn: async () => {
      console.log('🎯 [useAvailableAds] Buscando anúncios e aliases');

      // 1. Obter clínica atual se não estiver em modo admin
      let clinicaId = targetClinicaId;

      if (!adminMode && !clinicaId) {
        const { data: userData } = await supabase.auth.getUser();
        if (userData?.user) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('clinica_id')
            .eq('user_id', userData.user.id)
            .single();
          clinicaId = profile?.clinica_id;
        }
      }

      console.log(`🏥 [useAvailableAds] Usando clinica_id: ${clinicaId}`);

      // 2. Buscar leads com anúncios - OTIMIZADO
      // Limita a 1000 registros mais recentes para reduzir tráfego
      // (anúncios únicos geralmente aparecem nos leads mais recentes)
      let query = supabase
        .from('leads')
        .select('ad_name, ad_platform')
        .not('ad_name', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1000); // Limita a 1000 mais recentes

      // Filtrar por clínica se disponível
      if (clinicaId) {
        query = query.eq('clinica_id', clinicaId);
      }

      const { data: leadsData, error: leadsError } = await query;

      if (leadsError) {
        console.error('❌ [useAvailableAds] Erro ao buscar leads:', leadsError);
        throw new Error(`Erro ao buscar anúncios: ${leadsError.message}`);
      }

      console.log(`📊 [useAvailableAds] Encontrados ${leadsData?.length || 0} leads com anúncios`);

      // 3. Buscar aliases da clínica
      let aliasesData: any[] = [];

      if (clinicaId) {
        const { data: aliases, error: aliasError } = await supabase
          .from('ad_aliases')
          .select('ad_name_original, ad_alias')
          .eq('clinica_id', clinicaId);

        if (!aliasError && aliases) {
          aliasesData = aliases;
          console.log(`🏷️ [useAvailableAds] Encontrados ${aliasesData.length} aliases`);
        }
      }

      // 4. Criar mapa de aliases para lookup rápido
      const aliasMap = new Map();
      aliasesData.forEach(alias => {
        aliasMap.set(alias.ad_name_original, alias.ad_alias);
      });

      // 5. Processar anúncios únicos com aliases
      const uniqueAds = [...new Set(
        (leadsData || [])
          .map(lead => lead.ad_name)
          .filter(Boolean)
      )].sort().map(adName => {
        const alias = aliasMap.get(adName);
        return {
          value: adName,        // Valor original para query
          label: alias || adName // Alias ou nome original para exibição
        };
      });

      // 6. Processar plataformas únicas
      const uniquePlatforms = [...new Set(
        (leadsData || [])
          .map(lead => lead.ad_platform)
          .filter(Boolean)
      )].sort().map(platform => ({
        value: platform,
        label: platform
      }));

      console.log(`✅ [useAvailableAds] Processados:`, {
        anuncios: uniqueAds.length,
        comAlias: uniqueAds.filter(ad => ad.label !== ad.value).length,
        plataformas: uniquePlatforms.length
      });

      return {
        availableAds: uniqueAds,
        availablePlatforms: uniquePlatforms
      };
    },
    enabled: Boolean(targetClinicaId),
    staleTime: 300000, // Cache por 5 minutos
    refetchOnWindowFocus: false,
  });
};
